import { EmptyState } from "@/components/common/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAudit } from "@/hooks";
import { describeAudit, groupByDay } from "@/lib/audit";

/**
 * The organization's own record of what happened to it.
 *
 * Exists because we ask partners to trust us with named-employee reporting and
 * with their content — and a system that watches people should be watchable in
 * return. It is also the first thing anyone reaches for when a seat or a
 * document is not where they left it.
 */
export function ActivityPage() {
	const { data, isLoading } = useAudit(200);
	const entries = data?.entries ?? [];

	return (
		<>
			<PageHeader
				title="Activity log"
				description="Everything that has happened in your organization, and who did it."
			/>

			{isLoading ? (
				<Skeleton className="h-64 w-full" />
			) : entries.length === 0 ? (
				<EmptyState
					title="Nothing yet"
					description="Invites, seat changes, content and billing all appear here as they happen."
				/>
			) : (
				<div className="flex flex-col gap-6">
					{groupByDay(entries).map(([day, items]) => (
						<div key={day}>
							<h2 className="mb-2 text-caption font-medium uppercase tracking-wide text-muted-foreground">
								{day}
							</h2>
							<Card>
								<ul>
									{items.map((e) => {
										const { text, who } = describeAudit(e);
										return (
											<li
												key={e.id}
												className="flex items-baseline gap-3 border-b border-divider px-4 py-2.5 last:border-0"
											>
												<span className="tabular w-14 shrink-0 text-caption text-muted-foreground">
													{new Date(e.created_at).toLocaleTimeString(
														undefined,
														{
															hour: "2-digit",
															minute: "2-digit",
														},
													)}
												</span>
												<span className="flex-1 text-body text-heading">
													{text}
												</span>
												<span className="shrink-0 text-caption text-muted-foreground">
													{who}
												</span>
											</li>
										);
									})}
								</ul>
							</Card>
						</div>
					))}
				</div>
			)}
		</>
	);
}
